// Admin · Import trips via CSV/XLS — AI parses any format → MedexOVA canonical schema.
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, CheckCircle, Warning, ArrowClockwise, FileCsv } from "@phosphor-icons/react";
import { apiClient } from "../../lib/api";

const CANONICAL = [
  "patient_name", "patient_dob_last4", "contact_phone", "contact_email",
  "facility_name", "mobility_type", "oxygen_required", "attendant_required",
  "trip_type", "pickup_address", "pickup_suite_or_gate",
  "dropoff_address", "pickup_date", "pickup_time", "appointment_time",
  "return_trip", "return_time_or_willcall",
  "payer_type", "medicaid_id", "prior_auth_number", "notes",
];

export default function ImportTrips() {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [batches, setBatches] = useState([]);
  const [committing, setCommitting] = useState(false);
  const [sendConfirmations, setSendConfirmations] = useState(true);

  const loadBatches = async () => {
    try {
      const { data } = await apiClient.get("/import/batches");
      setBatches(data);
    } catch (_e) { /* */ }
  };
  useEffect(() => { loadBatches(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await apiClient.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success(`Parsed ${data.total_rows} rows · ${data.valid_rows} valid`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const { data } = await apiClient.post("/import/commit", {
        batch_id: preview.batch_id,
        send_confirmations: sendConfirmations,
      });
      toast.success(`Created ${data.created} bookings · ${data.failed} failed`);
      setPreview(null);
      await loadBatches();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div data-testid="import-trips-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-xl font-bold text-ink">Import trips</h2>
          <p className="text-xs text-slate-500 mt-0.5">Upload CSV or Excel — Ava maps any column format to MedexOVA's schema.</p>
        </div>
        <button onClick={loadBatches} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-ink">
          <ArrowClockwise size={12} /> Refresh
        </button>
      </div>

      {!preview && (
        <div className="border-2 border-dashed border-[#E2E0D8] rounded-xl p-12 text-center bg-white">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xls,.xlsx"
            hidden
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="import-file-input"
          />
          <UploadSimple size={32} weight="duotone" className="text-brandblue mx-auto" />
          <h3 className="mt-3 font-heading text-lg font-bold text-slate_navy">Drag a file here, or click to browse</h3>
          <p className="mt-1 text-xs text-slate-500">CSV, XLS, or XLSX · any column format</p>
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="import-browse-btn"
            className="mt-4 inline-flex items-center gap-2 bg-forest hover:bg-forest-hover text-white text-sm font-semibold px-5 py-2.5 rounded-md disabled:opacity-50"
          >
            <FileCsv size={14} /> {uploading ? "Uploading…" : "Choose file"}
          </button>
        </div>
      )}

      {preview && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="Rows" value={preview.total_rows} tone="bg-slate-100 text-slate_navy" />
            <Tile label="Valid" value={preview.valid_rows} tone="bg-forest/10 text-forest" />
            <Tile label="Errors" value={preview.total_rows - preview.valid_rows} tone="bg-terracotta/10 text-terracotta" />
            <Tile label="Mapped fields" value={Object.values(preview.mapping || {}).filter(Boolean).length} tone="bg-brandblue/10 text-brandblue" />
          </div>

          <div className="bg-white border border-[#E2E0D8] rounded-xl p-4">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate_navy mb-2">AI column mapping</div>
            <div className="grid sm:grid-cols-2 gap-1.5 text-xs">
              {CANONICAL.map((field) => {
                const src = preview.mapping?.[field];
                return (
                  <div key={field} className="flex items-center justify-between border-b border-slate-100 py-1">
                    <span className="font-mono text-slate-600">{field}</span>
                    {src ? <span className="text-brandblue font-semibold">{src}</span> : <span className="text-slate-300">—</span>}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white border border-[#E2E0D8] rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate_navy">Preview (first 20 rows)</div>
              <button onClick={() => setPreview(null)} className="text-xs text-slate-500 hover:text-ink">Clear</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-bold text-slate-500">#</th>
                    <th className="px-3 py-2 text-left font-bold text-slate-500">Status</th>
                    <th className="px-3 py-2 text-left font-bold text-slate-500">Patient</th>
                    <th className="px-3 py-2 text-left font-bold text-slate-500">Phone</th>
                    <th className="px-3 py-2 text-left font-bold text-slate-500">Date</th>
                    <th className="px-3 py-2 text-left font-bold text-slate-500">Pickup</th>
                    <th className="px-3 py-2 text-left font-bold text-slate-500">Drop-off</th>
                    <th className="px-3 py-2 text-left font-bold text-slate-500">Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.slice(0, 20).map((row, i) => {
                    const errs = preview.errors_by_row?.[i];
                    return (
                      <tr key={i} className={errs ? "bg-terracotta/5" : ""}>
                        <td className="px-3 py-1.5 text-slate-400">{i + 1}</td>
                        <td className="px-3 py-1.5">
                          {errs
                            ? <Warning size={14} weight="fill" className="text-terracotta" />
                            : <CheckCircle size={14} weight="fill" className="text-forest" />}
                        </td>
                        <td className="px-3 py-1.5">{row.patient_name || <em className="text-slate-300">—</em>}</td>
                        <td className="px-3 py-1.5">{row.contact_phone || <em className="text-slate-300">—</em>}</td>
                        <td className="px-3 py-1.5">{row.pickup_date} {row.pickup_time}</td>
                        <td className="px-3 py-1.5 truncate max-w-[180px]">{row.pickup_address || <em className="text-slate-300">—</em>}</td>
                        <td className="px-3 py-1.5 truncate max-w-[180px]">{row.dropoff_address || <em className="text-slate-300">—</em>}</td>
                        <td className="px-3 py-1.5 text-terracotta text-[10px]">{(errs || []).join(", ")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {preview.rows.length > 20 && <div className="px-4 py-2 text-[11px] text-slate-400">…and {preview.rows.length - 20} more rows</div>}
          </div>

          <div className="bg-brandblue/5 border border-brandblue/20 rounded-xl p-4 flex items-center justify-between gap-4">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={sendConfirmations}
                onChange={(e) => setSendConfirmations(e.target.checked)}
                data-testid="send-confirmations-toggle"
              />
              <span className="font-semibold text-slate_navy">Send confirmation outreach (SMS + email) to each rider</span>
            </label>
            <button
              onClick={commit}
              disabled={committing || preview.valid_rows === 0}
              data-testid="import-commit-btn"
              className="bg-forest hover:bg-forest-hover text-white font-bold px-6 py-2.5 rounded-md disabled:opacity-50"
            >
              {committing ? "Creating bookings…" : `Commit ${preview.valid_rows} valid rows`}
            </button>
          </div>
        </div>
      )}

      {!preview && batches.length > 0 && (
        <div className="mt-8">
          <h3 className="font-heading text-lg font-bold text-slate_navy mb-3">Recent imports</h3>
          <div className="bg-white border border-[#E2E0D8] rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-bold text-slate-500">File</th>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-bold text-slate-500">Created</th>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-bold text-slate-500">Status</th>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-bold text-slate-500">Rows</th>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-bold text-slate-500">Created</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id} className="border-t border-slate-100">
                    <td className="px-4 py-2 font-mono text-xs text-slate-700">{b.filename}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{new Date(b.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${
                        b.status === "committed" ? "bg-forest/10 text-forest" : "bg-amber-500/10 text-amber-700"
                      }`}>{b.status}</span>
                    </td>
                    <td className="px-4 py-2 text-xs">{b.valid_rows}/{b.total_rows}</td>
                    <td className="px-4 py-2 text-xs">{b.created_count || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, tone }) {
  return (
    <div className={`rounded-lg px-4 py-3 ${tone}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold">{label}</div>
      <div className="font-heading text-2xl font-black leading-none mt-1">{value}</div>
    </div>
  );
}
